import CollectionSearch  from "@/js/Shared/Collections/CollectionSearch";
import ContactLetterItem from "../Entities/ContactLetterItem";

export { ContactsFilterCollection as default };

/**
* Contacts Filter Collection
*
* Holds all contacts that need to be displayed on the screen
* applying the needed set filters
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsFilterCollection extends CollectionSearch {
  static MODIFIERS_MAP = {
    'add_group_members'   : 'modifyAddGroupMembers',
    'add_contact_letters' : 'modifyAddContactLetters',
    'add_contact_focus'   : 'modifyAddFocus',
  }

  static SEARCH_MAP = {
    'all'      : 'searchAll',
    'persons'  : 'searchAll',
    'groups'   : 'searchName',
    'frequent' : 'searchEmail',
  }

  /**
   * Collection modifiers
   *
   * @protected
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  modifiers = {};

  /**
   * Constructor
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  constructor() {
    super('id');
  }

  /**
   * Sets collection modifiers
   *
   * Available modifiers: type, search, person_focus, group_members, contact_letters
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} modifiers modifiers data
   *
   * @return {void}
   */
  setModifiers(modifiers) {
    this.modifiers = modifiers;
  }


  /**
   * Resets modifiers
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resetModifiers() {
    this.modifiers = {};
  }


  /**
   * Reloads the collection items based the set modifiers
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reload() {
    this.items = this.list.items;
    this.index = this.list.index;
    this.count = this.list.count;

    for (let modifier in ContactsFilterCollection.MODIFIERS_MAP) {
      if (!this.modifiers[modifier]) {
        continue;
      }

      this[ContactsFilterCollection.MODIFIERS_MAP[modifier]](this.modifiers[modifier]);
    }
  }

  /**
   * Modifies the list to add groups members to the group list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} group_ids group_ids
   *
   * @return {void}
   */
  modifyAddGroupMembers(group_ids) {
    let item = this.createModificationItem();

    this.list.items.forEach((contact) => {
      this.addObjectToModifiedItem(item, contact);

      if (group_ids[contact.id]) {
        for (let member in this.list.index[contact.id].members) {
          this.addObjectToModifiedItem(
            item, this.list.index[contact.id].members[member]
          )
        }
      }
    });

    this.applyModifiedItem(item);
  }

  /**
   * Modifies the list to add contacts to the top of the list
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Array} contacts Contacts to focus
   *
   * @return {void}
   */
  modifyAddFocus(contacts) {
    if (!contacts) {
      return;
    }

    let modified = this.createModificationItem();

    contacts.forEach(contact => {
      this.addObjectToModifiedItem(modified, contact);
    });

    this.list.items.forEach((contact) => {
      if (modified.index[contact.id]) {
        return;
      }

      this.addObjectToModifiedItem(modified, contact);

    });

    this.applyModifiedItem(modified);
  }

  /**
   * Modifies the list to add a letter dummy contact,
   * before the first contact starting with that letter
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} contacts contacts list object
   *
   * @return {void}
   */
  modifyAddContactLetters() {
    let item    = this.createModificationItem();
    let letters = {};

    this.list.items.forEach((contact) => {
      let char = contact.name.charAt(0).toLowerCase();

      if (char.search(/[^a-z]+/) === 0) {
        char = '#';
      }

      if (!letters[char]) {
        let letter      = new ContactLetterItem();
        letter.name     = char;
        letter.id       = char;
        letters[char]   = true;
        this.addObjectToModifiedItem(item, letter);
      }

      this.addObjectToModifiedItem(item, contact);
    })

    this.applyModifiedItem(item);
  }

  /**
   * Search in contacts
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} type  contacts type persons | groups | frequent
   * @param {String} term  search term
   * @param {Number} limit search limit
   *
   * @return {void}
   */
  searchIn(type, term, limit) {
    this[ContactsFilterCollection.SEARCH_MAP[type]](term, limit);
  }

  /**
   * Searchs All fields
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} term  search term
   * @param {Number} limit search limit
   *
   * @return {void}
   */
  searchAll(term, limit = 0) {
    this.search(term, {
      'email'        : ['lower_case',],
      'name'         : ['lower_case', 'latinize'],
      'mobile_phone' : ['lower_case',],
      'home_address' : ['lower_case',],
      'company'      : ['lower_case',]
    }, limit);
  }

  /**
   * Searchs Contacts Name
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} term  search term
   * @param {Number} limit search limit

  *
  * @return {void}
  */
  searchName(term, limit = 50) {
    this.search(term, {
      'name' : ['lower_case', 'latinize'],
    }, limit);
  }

  /**
   * Search Contacts Email
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} term  serach term
   * @param {Number} limit search limit
   *
   * @return {void}
   */
  searchEmail(term, limit = 50) {
    this.search(term, {
      'email' : ['lower_case',],
    }, limit);
  }

  /**
   * Resets collection to it's initial state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  reset() {
    this.resetModifiers();
    super.reset();
  }
}